import { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { X, Plus, Trash2, Search, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../hooks/useConfirm';
import ConfirmDialog from '../common/ConfirmDialog';

export default function ManageAccessModal({ isOpen, onClose, student }) {
  const toast = useToast();
  const confirmDialog = useConfirm();
  const [modules, setModules] = useState([]);
  const [studentAccess, setStudentAccess] = useState([]);
  const [loading, setLoading] = useState(true);
  const [granting, setGranting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModules, setSelectedModules] = useState(new Set());
  const [isBulkMode, setIsBulkMode] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Carregar todos os módulos
      const { data: modulesData } = await api.get('/modules');
      setModules(modulesData || []);

      // Carregar acessos do aluno
      const { data: accessData } = await api.get(`/students/${student.id}/access`);
      setStudentAccess(accessData || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  }, [student]);

  useEffect(() => {
    if (isOpen && student) {
      loadData();
      setSelectedModules(new Set());
      setIsBulkMode(false);
    }
  }, [isOpen, student, loadData]);

  async function handleGrantAccess(moduleId) {
    setGranting(true);
    try {
      await api.post(`/students/${student.id}/access`, {
        module_id: moduleId
      });
      console.log('✅ Acesso concedido');
      toast.success('Acesso concedido com sucesso!');
      await loadData(); // Recarregar dados
    } catch (error) {
      console.error('❌ Erro ao conceder acesso:', error);
      toast.error('Erro ao conceder acesso');
    } finally {
      setGranting(false);
    }
  }

  async function handleRevokeAccess(moduleId) {
    const confirmed = await confirmDialog.confirm({
      title: 'Revogar Acesso',
      message: 'Deseja realmente revogar o acesso a este módulo?',
      confirmText: 'Revogar',
      cancelText: 'Cancelar',
      type: 'danger'
    });

    if (!confirmed) return;

    try {
      await api.delete(`/students/${student.id}/access/${moduleId}`);
      console.log('✅ Acesso revogado');
      toast.success('Acesso revogado com sucesso!');
      await loadData(); // Recarregar dados
    } catch (error) {
      console.error('❌ Erro ao revogar acesso:', error);
      toast.error('Erro ao revogar acesso');
    }
  }

  function hasAccess(moduleId) {
    return studentAccess.some(access => access.module_id === moduleId);
  }

  function toggleModuleSelection(moduleId) {
    const newSelected = new Set(selectedModules);
    if (newSelected.has(moduleId)) {
      newSelected.delete(moduleId);
    } else {
      newSelected.add(moduleId);
    }
    setSelectedModules(newSelected);
  }

  function selectAll() {
    const allModuleIds = modules
      .filter(m => !hasAccess(m.id))
      .map(m => m.id);
    setSelectedModules(new Set(allModuleIds));
  }

  function deselectAll() {
    setSelectedModules(new Set());
  }

  async function handleBulkGrant() {
    if (selectedModules.size === 0) {
      toast.warning('Selecione pelo menos um módulo');
      return;
    }

    setGranting(true);
    try {
      const promises = Array.from(selectedModules).map(moduleId =>
        api.post(`/students/${student.id}/access`, { module_id: moduleId })
      );
      
      await Promise.all(promises);
      
      toast.success(`${selectedModules.size} módulo(s) concedido(s) com sucesso!`);
      setSelectedModules(new Set());
      setIsBulkMode(false);
      await loadData();
    } catch (error) {
      console.error('❌ Erro ao conceder acessos:', error);
      toast.error('Erro ao conceder alguns acessos');
    } finally {
      setGranting(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative bg-background-800 border border-gray-700 rounded-3xl p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-2xl font-bold text-text-primary">
                Gerenciar Acesso aos Módulos
              </h2>
              <p className="text-text-secondary mt-1 flex items-center gap-2">
                <span className="font-medium text-primary-500">{student?.full_name}</span>
                <span className="text-gray-600">•</span>
                <span>{student?.email}</span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-background-700 rounded-lg"
            >
              <X size={24} />
            </button>
          </div>

          {/* Bulk Mode Toggle */}
          <div className="flex items-center justify-between mb-4 p-4 bg-background-900 rounded-2xl border-2 border-gray-700">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isBulkMode}
                  onChange={(e) => {
                    setIsBulkMode(e.target.checked);
                    if (!e.target.checked) {
                      setSelectedModules(new Set());
                    }
                  }}
                  className="w-5 h-5 rounded border-gray-700 text-primary-500 focus:ring-primary-500"
                />
                <span className="text-sm font-semibold text-text-primary">
                  Modo de Seleção Múltipla
                </span>
              </label>
              
              {isBulkMode && selectedModules.size > 0 && (
                <span className="text-sm text-primary-500 font-medium">
                  {selectedModules.size} selecionado(s)
                </span>
              )}
            </div>

            {isBulkMode && (
              <div className="flex gap-2">
                <button
                  onClick={selectAll}
                  className="text-sm px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all"
                >
                  Selecionar Todos
                </button>
                <button
                  onClick={deselectAll}
                  className="text-sm px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all"
                >
                  Limpar
                </button>
              </div>
            )}
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar módulos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-background-900 border-2 border-gray-700 rounded-2xl focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20 transition-all text-text-primary placeholder-gray-500"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
            {modules.length === 0 ? (
              <p className="text-center text-text-secondary py-8">
                Nenhum módulo disponível
              </p>
            ) : (
              modules
                .filter(module => 
                  module.title_pt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  module.description_pt?.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((module) => {
                  const hasModuleAccess = hasAccess(module.id);
                  const isSelected = selectedModules.has(module.id);
                  const canSelect = isBulkMode && !hasModuleAccess;
                  
                  return (
                    <motion.div
                      key={module.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      onClick={() => canSelect && toggleModuleSelection(module.id)}
                      className={`group relative overflow-hidden rounded-2xl border-2 transition-all ${
                        hasModuleAccess
                          ? 'bg-success/5 border-success/30'
                          : isSelected
                          ? 'bg-primary-500/10 border-primary-500'
                          : 'bg-background-900 border-gray-700 hover:border-gray-600'
                      } ${canSelect ? 'cursor-pointer' : ''}`}
                    >
                      <div className="flex items-center gap-4 p-4">
                        {/* Checkbox for bulk mode */}
                        {isBulkMode && !hasModuleAccess && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleModuleSelection(module.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-5 h-5 rounded border-gray-700 text-primary-500 focus:ring-primary-500 cursor-pointer"
                          />
                        )}
                        
                        {/* Status Indicator */}
                        <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
                          hasModuleAccess
                            ? 'bg-success/20'
                            : 'bg-background-800'
                        }`}>
                          {hasModuleAccess ? (
                            <CheckCircle size={24} className="text-success" />
                          ) : (
                            <div className="w-6 h-6 rounded-full border-2 border-gray-600"></div>
                          )}
                        </div>

                        {/* Module Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-text-primary flex items-center gap-2">
                            {module.title_pt}
                            {hasModuleAccess && (
                              <span className="text-xs font-semibold px-2 py-0.5 bg-success/20 text-success rounded-full">
                                Ativo
                              </span>
                            )}
                          </h3>
                          <p className="text-sm text-text-secondary mt-1 line-clamp-1">
                            {module.description_pt || 'Sem descrição'}
                          </p>
                        </div>

                        {/* Action Button */}
                        {!isBulkMode && (
                          <div className="flex-shrink-0">
                            {hasModuleAccess ? (
                              <button
                                onClick={() => handleRevokeAccess(module.id)}
                                className="flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-xl transition-all font-medium"
                              >
                                <Trash2 size={16} />
                                Revogar
                              </button>
                            ) : (
                              <button
                                onClick={() => handleGrantAccess(module.id)}
                                disabled={granting}
                                className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-all disabled:opacity-50 font-medium shadow-lg shadow-primary-500/20"
                              >
                                <Plus size={16} />
                                Conceder
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 pt-6 border-t border-gray-700">
          {isBulkMode && selectedModules.size > 0 ? (
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setIsBulkMode(false);
                  setSelectedModules(new Set());
                }}
                className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-2xl font-semibold transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleBulkGrant}
                disabled={granting}
                className="flex-1 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-2xl font-semibold transition-all disabled:opacity-50 shadow-lg shadow-primary-500/20"
              >
                {granting ? 'Salvando...' : `Conceder Acesso (${selectedModules.size})`}
              </button>
            </div>
          ) : (
            <button
              onClick={onClose}
              className="w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-2xl font-semibold transition-all"
            >
              Fechar
            </button>
          )}
        </div>
      </motion.div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={confirmDialog.handleCancel}
        onConfirm={confirmDialog.handleConfirm}
        {...confirmDialog.config}
      />
    </div>
  );
}
